import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CompensationTypes } from '@app/constants/compensation-types';
import { EmploymentTypes } from '@app/constants/employment-types';
import { MergeFieldFormInputTypes } from '@app/enums';
import { Department } from '@app/models/company/department.model';
import { Job } from '@app/models/company/job.model';
import { Office } from '@app/models/company/office.model';
import { Model } from '@app/models/core/base.model';
import { CompanyDocument } from '@app/models/documents/company-document.model';
import { SigningTemplateSlotNames } from '@app/modules/documents-v2/enums/signing-template-slot-names.enum';
import { MergeFieldName, SigningTemplateService } from '@app/modules/documents-v2/services/signing-template.service';
import { MaybeCurrencyPipe } from '@app/pipes/numbers/maybe-currency.pipe';
import { AuthService } from '@app/services';
import { SigningTemplateSlot } from '@models/documents/signing-template-slot.model';
import { OfferLetter } from '@models/offer-letters/offer-letter.model';
import { SignerSlot } from '@models/offer-letters/signer-slots';
import {
    buildOfferLetterFieldAttributes,
    EXPIRES_ON_DATE_FIELD,
    MergeFieldDropdownInputAttributes,
    MergeFieldFormInputAttributes,
    mergeFieldFormInputAttributesContainsExpiresOn,
} from '../functions/offer-letter-field-form-input-attributes';

type CandidateDropdownFieldsWithOptions = { [key: string]: string[] | Model[] };

@Injectable()
export class OfferLettersService {
    private offices: Office[] = [];
    private departments: Department[] = [];
    private jobs: Job[] = [];
    private employmentTypes: string[] = EmploymentTypes;
    private compensationTypes = CompensationTypes;

    protected candidateDropdownFieldsWithOptions: CandidateDropdownFieldsWithOptions = {};

    constructor(
        private http: HttpClient,
        private auth: AuthService,
        private signingTemplateService: SigningTemplateService,
        private maybeCurrencyPipe: MaybeCurrencyPipe
    ) {
        this.setDropDownOptions();
    }

    async getMergeFields(offerLetterTemplate: CompanyDocument): Promise<MergeFieldName[]> {
        const signingTemplate = offerLetterTemplate.signingTemplate;

        return this.signingTemplateService.fetchMergeFields(offerLetterTemplate, signingTemplate);
    }

    // This allows us to create signer slots for the purpose of saving cosigners
    buildSignerSlots(slots: SigningTemplateSlot[]): SignerSlot[] {
        return slots
            .filter((slot) => slot.name !== SigningTemplateSlotNames.Candidate)
            .map((slot) => {
                const order = slot.order == 0 ? null : slot.order;
                return {
                    slotId: slot.id,
                    employeeId: slot.assignee.id,
                    order,
                };
            });
    }

    buildMergeFieldFormInputAttributes(mergeFields: string[]): MergeFieldFormInputAttributes[] {
        const attributes: MergeFieldFormInputAttributes[] = mergeFields.map(
            (mergeField: string): MergeFieldFormInputAttributes => {
                const mergeFieldFormInputAttributes = buildOfferLetterFieldAttributes(mergeField);

                if (mergeFieldFormInputAttributes.inputType === MergeFieldFormInputTypes.dropdown) {
                    mergeFieldFormInputAttributes.dropDownOptions =
                        this.getMergeFieldDropDownInputAttributes(mergeField);
                }

                return mergeFieldFormInputAttributes;
            }
        );

        // if expires on was not in merge fields, include it as an input
        // but do not make it required
        if (!mergeFieldFormInputAttributesContainsExpiresOn(mergeFields)) {
            const expiresOnAttributes = buildOfferLetterFieldAttributes(EXPIRES_ON_DATE_FIELD);
            expiresOnAttributes.required = false;

            attributes.unshift(expiresOnAttributes);
        }

        return attributes;
    }

    async getClaimUrl(offerLetter: OfferLetter): Promise<string> {
        const endpoint = `/v2/signingTemplates/${offerLetter.signingTemplate.id}/candidateClaimUrl`;
        const payload = {
            data: {
                type: 'signingTemplates',
                attributes: {
                    ...this.getOfferLetterAttributes(offerLetter),
                },
            },
        };

        try {
            const res = await this.http
                .post<{ data: { attributes: { claimUrl: string } } }>(endpoint, payload)
                .toPromise();

            return res.data.attributes.claimUrl;
        } catch (e) {
            return;
        }
    }

    /*
     * Building the list of OfferLetter attributes to avoid a circular dependency
     * when using .getAttributes()
     */
    private getOfferLetterAttributes(offerLetter: OfferLetter): Record<string, unknown> {
        const record = {
            signingTemplateId: offerLetter.signingTemplateId,
            reportsToId: offerLetter.reportsToId,
            candidateId: offerLetter.candidateId,
            email: offerLetter.email,
            legalFirstName: offerLetter.legalFirstName,
            legalLastName: offerLetter.legalLastName,
            positionId: offerLetter.positionId,
            departmentId: offerLetter.departmentId,
            locationId: offerLetter.locationId,
            employmentType: offerLetter.employmentType,
            compensationRate: this.maybeCurrencyPipe.transform(offerLetter.compensationRate),
            compensationFrequency: offerLetter.compensationFrequency,
            stockOptions: offerLetter.stockOptions,
            probationPeriod: offerLetter.probationPeriod,
            contractTerm: offerLetter.contractTerm,
            candidateAddress: offerLetter.candidateAddress,
            hoursPerWeek: offerLetter.hoursPerWeek,
            firstDayOfWorkOn: offerLetter.firstDayOfWorkOn,
            contractEndsOn: offerLetter.contractEndsOn,
            vacationDays: offerLetter.vacationDays,
            expiresOn: offerLetter.expiresOn,
            signedAt: offerLetter.signedAt,
            declinedAt: offerLetter.declinedAt,
            withdrawnAt: offerLetter.withdrawnAt,
        };

        if (offerLetter.signingTemplate.slots.length) {
            record['signerSlots'] = this.buildSignerSlots(offerLetter.signingTemplate.slots);
        }

        return record;
    }

    private getMergeFieldDropDownInputAttributes(mergeField: string): MergeFieldDropdownInputAttributes {
        const candidateDropdownFieldsWithAddNewModelOption = {
            'Candidate - Position': {
                showAddButton: true,
                addButtonLabel: 'offer-letter.merge-fields.add-button.newPosition',
            },
            'Candidate - Department': {
                showAddButton: true,
                addButtonLabel: 'offer-letter.merge-fields.add-button.newDepartment',
            },
            'Candidate - Office Location': {
                showAddButton: true,
                addButtonLabel: 'offer-letter.merge-fields.add-button.newOffice',
            },
            'Candidate - Employment Type': {
                showAddButton: false,
            },
            'Candidate - Compensation Frequency': {
                showAddButton: false,
            },
        };

        const mergeFieldDropDownInputAttributes: MergeFieldDropdownInputAttributes = {
            options: this.getDropDownOptions(mergeField),
            optionLabel: this.getDropdownOptionLabel(mergeField),
            showAddButton: candidateDropdownFieldsWithAddNewModelOption[mergeField].showAddButton,
            addButtonLabel: candidateDropdownFieldsWithAddNewModelOption[mergeField].addButtonLabel
                ? candidateDropdownFieldsWithAddNewModelOption[mergeField].addButtonLabel
                : '',
        };

        return mergeFieldDropDownInputAttributes;
    }

    getDropDownOptions(mergeField: string): string[] | Model[] {
        return this.candidateDropdownFieldsWithOptions[mergeField];
    }

    protected async setDropDownOptions(): Promise<void> {
        await Promise.all([this.getOffices(), this.getDepartments(), this.getPositions()]);

        this.candidateDropdownFieldsWithOptions = {
            'Candidate - Position': this.jobs,
            'Candidate - Department': this.departments,
            'Candidate - Office Location': this.offices,
            'Candidate - Employment Type': this.employmentTypes,
            'Candidate - Compensation Frequency': Object.keys(this.compensationTypes),
        };
    }

    private getDropdownOptionLabel(mergeField: string): string {
        const candidateDropdownOptionLabels = {
            'Candidate - Position': 'title',
            'Candidate - Department': 'name',
            'Candidate - Office Location': 'name',
        };

        return candidateDropdownOptionLabels[mergeField] ?? null;
    }

    private async getOffices(): Promise<void> {
        const [offices] = await Office.param('company', this.auth.company.id).all();
        this.offices = offices;
    }

    private async getDepartments(): Promise<void> {
        const [departments] = await Department.param('company', this.auth.company.id).orderBy('name', 'ASC').all();
        this.departments = departments;
    }

    private async getPositions(): Promise<void> {
        const [jobs] = await Job.param('company', this.auth.company.id).orderBy('title', 'ASC').all();
        this.jobs = jobs;
    }
}
